import api from "../api";
import { getCurrentMonthDateRange } from "../../utils/dateRange";
import {
  DashboardFilter,
  DashboardRequestPayload,
  FinancialEvolution,
  ExpenseCategory,
  RevenueCategory,
  KPIData,
  DREHierarchicalResponse,
  DREExpandRequest,
  DREExpandResponse,
  DRENodeType,
  UploadFileResponse,
} from "../../types/dashboard";

const buildRequestPayload = (filters?: DashboardFilter): DashboardRequestPayload => {
  const { startDate: defaultStartDate, endDate: defaultEndDate } = getCurrentMonthDateRange();

  const payload: DashboardRequestPayload = {
    accountId: "",
    startDate: filters?.startDate || defaultStartDate,
    endDate: filters?.endDate || defaultEndDate,
  };

  if (filters?.categoryIds) payload.categoryIds = filters.categoryIds.join(",");
  if (filters?.clientIds) payload.clientIds = filters.clientIds.join(",");
  if (filters?.vehicleIds) payload.vehicleIds = filters.vehicleIds.join(",");
  if (filters?.contracts) payload.contracts = filters.contracts.join(",");

  return payload;
};

/**
 * Build payload for POST requests (JSON body).
 * Arrays are sent as actual arrays, not comma-separated strings.
 * This is required for DRE expand endpoints.
 */
interface PostRequestPayload {
  startDate: string;
  endDate: string;
  categoryIds?: number[];
  clientIds?: number[];
  vehicleIds?: number[];
  contracts?: string[];
}

const buildPostRequestPayload = (filters?: DashboardFilter): PostRequestPayload => {
  const { startDate: defaultStartDate, endDate: defaultEndDate } = getCurrentMonthDateRange();

  const payload: PostRequestPayload = {
    startDate: filters?.startDate || defaultStartDate,
    endDate: filters?.endDate || defaultEndDate,
  };

  // Convert string arrays to number arrays for POST body
  if (filters?.categoryIds?.length) {
    payload.categoryIds = filters.categoryIds.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id));
  }
  if (filters?.clientIds?.length) {
    payload.clientIds = filters.clientIds.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id));
  }
  if (filters?.vehicleIds?.length) {
    payload.vehicleIds = filters.vehicleIds.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id));
  }
  if (filters?.contracts?.length) {
    payload.contracts = filters.contracts;
  }

  return payload;
};

export const dashboardService = {
  getKPI: async (filters?: DashboardFilter): Promise<KPIData> => {
    try {
      // Use POST with JSON body - arrays must be sent as arrays, not comma-separated strings
      const payload = buildPostRequestPayload(filters);
      const response = await api.post("/dre/summary", payload);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching KPI:", error);
      throw error;
    }
  },
  getVolksTotalDRE: async (filters?: DashboardFilter): Promise<DREHierarchicalResponse> => {
    try {
      // Use POST with JSON body - arrays must be sent as arrays, not comma-separated strings
      const payload = buildPostRequestPayload(filters);
      const response = await api.post("/dre/complete", payload);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching DRE:", error);
      throw error;
    }
  },
  /**
   * Upload de arquivo Excel com processamento assíncrono.
   * Retorna imediatamente com status PROCESSING e faz polling até completar.
   */
  uploadExpenseFile: async (
    file?: File | null,
    onProgress?: (status: string, processedRows: number, totalRows: number) => void
  ): Promise<UploadFileResponse | null> => {
    try {
      if (!file) return null;

      const formData = new FormData();
      formData.append("file", file);

      // 1. Envia o arquivo (retorna 202 Accepted imediatamente)
      const uploadResponse = await api.post("/uploads", formData);
      const uploadId = uploadResponse.data?.data?.id;

      if (!uploadId) {
        console.error("Upload failed: no upload ID returned");
        return null;
      }

      // 2. Polling até o upload completar
      const pollInterval = 2000; // 2 segundos
      const maxAttempts = 300; // 10 minutos máximo
      let attempts = 0;

      while (attempts < maxAttempts) {
        const statusResponse = await api.get(`/uploads/${uploadId}`);
        const uploadData = statusResponse.data?.data;
        const status = uploadData?.status;

        // Notifica progresso
        if (onProgress && uploadData) {
          onProgress(status, uploadData.processedRows || 0, uploadData.totalRows || 0);
        }

        // Verifica se terminou
        if (status === "COMPLETED" || status === "PARTIAL" || status === "FAILED") {
          // Busca erros se houver
          let errors: any[] = [];
          if (uploadData.errorRows > 0) {
            try {
              const errorsResponse = await api.get(`/uploads/${uploadId}/errors`);
              errors = errorsResponse.data?.data?.content || [];
            } catch (e) {
              console.warn("Could not fetch upload errors:", e);
            }
          }

          return {
            status: statusResponse.data.status,
            message: statusResponse.data.message,
            data: {
              upload: uploadData,
              errors: errors,
            },
            timestamp: statusResponse.data.timestamp,
          };
        }

        // Aguarda antes do próximo polling
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        attempts++;
      }

      // Timeout - retorna o último status conhecido
      console.error("Upload polling timeout");
      return null;
    } catch (error) {
      console.error("Error uploading expense file:", error);
      return null;
    }
  },
  getDRE: async (filters?: DashboardFilter): Promise<DREHierarchicalResponse> => {
    try {
      const payload = buildRequestPayload(filters);
      const response = await api.post("/dashboard/dre/hierarchical", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching DRE:", error);
      throw error;
    }
  },

  getFinancialEvolution: async (filters?: DashboardFilter): Promise<FinancialEvolution[]> => {
    try {
      const filter = buildRequestPayload(filters);

      const payload = {
        ...filter,
        ...(filter?.categoryIds ? { categoryIds: filter?.categoryIds?.split(",") } : {}),
        ...(filter?.clientIds ? { clientIds: filter?.clientIds?.split(",") } : {}),
        ...(filter?.contracts ? { contracts: filter?.contracts?.split(",") } : {}),
        ...(filter?.vehicleIds ? { vehicleIds: filter?.vehicleIds?.split(",") } : {}),
      };

      const response = await api.post("/dashboard/financial-evolution", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching financial evolution:", error);
      throw error;
    }
  },

  getExpenses: async (filters?: DashboardFilter): Promise<any> => {
    try {
      const payload = buildRequestPayload(filters);
      const response = await api.post("/dashboard/expenses", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching expenses:", error);
      throw error;
    }
  },

  getRevenues: async (filters?: DashboardFilter): Promise<any> => {
    try {
      const payload = buildRequestPayload(filters);
      const response = await api.post("/dashboard/revenues", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching revenues:", error);
      throw error;
    }
  },

  getExpenseCategories: async (filters?: DashboardFilter): Promise<ExpenseCategory[]> => {
    try {
      const filter = buildRequestPayload(filters);

      const payload = {
        ...filter,
        ...(filter?.categoryIds ? { categoryIds: filter?.categoryIds?.split(",") } : {}),
        ...(filter?.clientIds ? { clientIds: filter?.clientIds?.split(",") } : {}),
        ...(filter?.contracts ? { contracts: filter?.contracts?.split(",") } : {}),
        ...(filter?.vehicleIds ? { vehicleIds: filter?.vehicleIds?.split(",") } : {}),
      };

      const response = await api.post("/dashboard/expense/categories", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching expense categories:", error);
      throw error;
    }
  },

  getRevenueCategories: async (filters?: DashboardFilter): Promise<RevenueCategory[]> => {
    try {
      const filter = buildRequestPayload(filters);

      const payload = {
        ...filter,
        ...(filter?.categoryIds ? { categoryIds: filter?.categoryIds?.split(",") } : {}),
        ...(filter?.clientIds ? { clientIds: filter?.clientIds?.split(",") } : {}),
        ...(filter?.contracts ? { contracts: filter?.contracts?.split(",") } : {}),
        ...(filter?.vehicleIds ? { vehicleIds: filter?.vehicleIds?.split(",") } : {}),
      };

      const response = await api.post("/dashboard/revenue/categories", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching revenue categories:", error);
      throw error;
    }
  },

  /**
   * Lazy loading for DRE hierarchy.
   * Calls different endpoints based on the node type being expanded:
   * - CATEGORIA → /dre/expand/category/{id}/clients
   * - CLIENTE → /dre/expand/category/{categoryId}/client/{clientId}/contracts
   * - CONTRACT → /dre/expand/category/{categoryId}/client/{clientId}/contract/{contract}/vehicles
   */
  expandDRENode: async (filters: DashboardFilter, expandRequest: DREExpandRequest): Promise<DREExpandResponse> => {
    try {
      // Use POST payload with arrays (not comma-separated strings) for DRE expand endpoints
      const payload = buildPostRequestPayload(filters);
      const { nodeId, nodeType, lineType, parentPath } = expandRequest;

      // Determine the backend type (RECEITA or DESPESA) from lineType
      const backendType = lineType === "RECEITA_BRUTA" || lineType === "RECEITA_LIQUIDA" ? "RECEITA" : "DESPESA";

      let endpoint = "";
      let response;

      switch (nodeType) {
        case "CATEGORIA": {
          // Expanding a category → load clients
          endpoint = `/dre/expand/category/${nodeId}/clients?type=${backendType}`;
          response = await api.post(endpoint, payload);
          break;
        }
        case "CLIENTE":
        case "FORNECEDOR": {
          // Expanding a client → load contracts
          const categoryId = parentPath?.categoryId;
          endpoint = `/dre/expand/category/${categoryId}/client/${nodeId}/contracts?type=${backendType}`;
          response = await api.post(endpoint, payload);
          break;
        }
        default: {
          // Expanding a contract → load vehicles
          const catId = parentPath?.categoryId;
          const clientId = parentPath?.clientOrSupplierId;
          // nodeId is the contract string
          endpoint = `/dre/expand/category/${catId}/client/${clientId}/contract/${encodeURIComponent(nodeId)}/vehicles?type=${backendType}`;
          response = await api.post(endpoint, payload);
          break;
        }
      }

      // Transform backend response to frontend format
      const children = response.data?.data || [];
      return {
        nodeId,
        children: children.map((item: any) => ({
          id: String(item.id),
          nodeType: getNodeTypeFromLevel(nodeType),
          label: item.label,
          value: item.value,
          formattedValue: item.formattedValue,
          level: getNextLevel(nodeType),
          expandable: item.expandable,
          expanded: false,
          children: undefined,
        })),
      };
    } catch (error) {
      console.error("Error expanding DRE node:", error);
      throw error;
    }
  },
};

// Helper to determine next node type in hierarchy
function getNodeTypeFromLevel(currentType: DRENodeType): DRENodeType {
  switch (currentType) {
    case "CATEGORIA":
      return "CLIENTE";
    case "CLIENTE":
    case "FORNECEDOR":
      return "VIAGEM"; // Contract level
    default:
      return "VEICULO";
  }
}

// Helper to determine next level depth
function getNextLevel(currentType: DRENodeType): number {
  switch (currentType) {
    case "CATEGORIA":
      return 2; // Clients are at level 2
    case "CLIENTE":
    case "FORNECEDOR":
      return 3; // Contracts are at level 3
    default:
      return 4; // Vehicles are at level 4
  }
}

export default dashboardService;
